import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { ArrowRight, Lock, Mic, BookOpen, PencilLine, Headphones, Loader2, Star } from "lucide-react";

const SKILL_ICON = {
  speaking: Mic, reading: BookOpen, writing: PencilLine, listening: Headphones,
};

const SKILL_COLOR = {
  speaking: "#F77F00", reading: "#0EA5E9", writing: "#16A34A", listening: "#A855F7",
};

export default function ExamHub({ examIdOverride }) {
  const params = useParams();
  const examId = examIdOverride || params.examId;
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/exam/${examId}`).then((r) => { if (!cancel) { setExam(r.data); setLoading(false); }})
      .catch(() => setLoading(false));
    return () => { cancel = true; };
  }, [examId]);

  if (loading || !exam) {
    return (
      <DashboardLayout>
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading exam…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid={`exam-hub-${examId}`}>
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl p-10 text-white"
          style={{ background: `linear-gradient(135deg, ${exam.color} 0%, #003049 100%)` }}>
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">{exam.subtitle}</div>
              <h1 className="font-display text-5xl mb-3">{exam.name}</h1>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="px-4 py-2 rounded-full bg-white/15 backdrop-blur">{exam.duration}</div>
                <div className="px-4 py-2 rounded-full bg-white/15 backdrop-blur">Score: {exam.score}</div>
                <div className="px-4 py-2 rounded-full bg-white/15 backdrop-blur">AI-graded · Whisper + GPT-4o</div>
              </div>
            </div>
            <Link to={`/app/${examId}/mock`} data-testid={`begin-mock-cta-${examId}`}
              className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#003049] font-semibold hover:scale-105 transition-transform shadow-lg">
              <Star className="w-4 h-4 text-[#F77F00]" />
              Take Full Mock Test
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </Link>
          </div>
        </div>

        {/* Question types grid */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-2xl text-[#003049]">Question Types</h2>
            <Link to="/app" className="text-sm text-slate-500 hover:text-[#F77F00]">← Back to dashboard</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {exam.types.map((t) => {
              const Icon = SKILL_ICON[t.skill] || BookOpen;
              const color = SKILL_COLOR[t.skill] || "#003049";
              const to = `/app/${examId}/${t.id}`;
              const Card = t.locked ? "div" : Link;
              const cardProps = t.locked ? {} : { to };
              return (
                <Card key={t.id} {...cardProps}
                  data-testid={`type-card-${examId}-${t.id}`}
                  className={`group relative bg-white rounded-2xl border border-slate-200 p-5 ${
                    t.locked ? "opacity-60 cursor-not-allowed" : "hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  }`}>
                  {t.locked && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                      <Lock className="w-3 h-3" /> PREMIUM
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: `${color}1A`, color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color }}>
                      {t.skill}
                    </div>
                  </div>
                  <div className="font-display text-lg text-[#003049] mb-1">{t.label}</div>
                  <div className="text-xs text-slate-500 mb-4">
                    {t.locked ? "Unlock with premium plan" : "Practice now · AI-graded in seconds"}
                  </div>
                  {!t.locked && (
                    <div className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color }}>
                      Start practice <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>

        {/* Recent attempts hint */}
        <div className="bg-gradient-to-r from-[#FFF4E5] to-[#FFE4E6] rounded-2xl p-5 flex items-center gap-4">
          <Star className="w-8 h-8 text-[#F77F00]" />
          <div>
            <div className="font-semibold text-[#003049]">Earn XP every time you practice</div>
            <div className="text-sm text-slate-600">+20 XP per speaking task · +15 XP per writing task · +10 XP per reading task</div>
          </div>
          <button onClick={() => nav("/app")} className="ml-auto px-4 py-2 rounded-full bg-[#003049] text-white text-sm hover:bg-[#0C4B66]">
            View dashboard
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
